import {
    Component,
    ContentChildren,
    QueryList,
    AfterContentInit,
    Input,
    Output,
    EventEmitter,
} from "@angular/core";
import { Validity } from "../store/validity/state";

declare interface SwitchEvent {
    tabTitle: string;
    tabCode: string;
}

@Component({
    selector: "zbd-tab",
    template: `
        <div *ngIf="active">
            <ng-content></ng-content>
        </div>
    `,
})
export class Tab {
    @Input("tabTitle") title: string;
    @Input() active = false;
    @Input() isValid = false;
    @Input("tabCode") code?: string;

    get validity(): Validity {
        return new Validity(this.isValid);
    }
}

@Component({
    selector: "zbd-tabbar",
    template: `
        <!-- appearance: tabs -->
        <ul class="zbd-tabbar" *ngIf="appearance === 'tabs'">
            <li
                *ngFor="let tab of tabs"
                class="zbd-tab"
                (click)="selectTab(tab)"
                [class.active]="tab.active"
            >
                <div class="left">{{ tab.title }}</div>
                <div class="right" *ngIf="showValidIndicators">
                    <span
                        class="zbd-validation-indicator"
                        [ngClass]="{
                            'icon-dot-indicator-three zbd-invalid':
                                !tab.isValid,
                            'icon-hook zbd-valid': tab.isValid,
                        }"
                    ></span>
                </div>
            </li>
        </ul>

        <!-- appearance: selector-strip -->
        <ng-container *ngIf="appearance === 'selector-strip'">
            <div class="zbd-selector-strip">
                <div
                    *ngFor="let tab of tabs"
                    class="zbd-selector zbd-button-like"
                    (click)="selectTab(tab)"
                    [class.active]="tab.active"
                >
                    <span class="zbd-left">{{ tab.title }}</span>
                    <zbd-validity-indicator
                        class="zbd-right"
                        *ngIf="showValidIndicators"
                        [validity]="tab.validity"
                    ></zbd-validity-indicator>
                </div>
            </div>
            <hr class="underline" *ngIf="tabs.length" />
        </ng-container>

        <ng-content></ng-content>
    `,
    styleUrls: ["zbd-selector-strip.component.css"],
    styles: [
        `
            .zbd-selector.zbd-button-like.active {
                border-top: thick solid #4066b3;
                padding-top: 2px;
            }
            .zbd-selector-strip {
                padding: 0 17px;
            }
            .underline {
                margin: 20px 20px 30px 20px;
            }
        `,
    ],
})
export class TabBar implements AfterContentInit {
    @ContentChildren(Tab) tabs: QueryList<Tab>;

    @Input() appearance: "tabs" | "selector-strip" = "tabs";
    @Input() showValidIndicators = true;
    @Input() switchStrategy: "switch-event" | "switch" = "switch";
    @Input() isRemoveable = false;
    @Output() select: EventEmitter<string> = new EventEmitter<string>();
    @Output() remove: EventEmitter<number> = new EventEmitter<number>();
    @Output() switch: EventEmitter<SwitchEvent> =
        new EventEmitter<SwitchEvent>();

    constructor() {}

    ngAfterContentInit(): void {
        // get all active tabs
        const activeTabs = this.tabs.filter((tab) => tab.active);

        // if there is no active tab set, activate the first if exists
        if (activeTabs.length === 0 && this.tabs.length > 0) {
            this.selectTab(this.tabs.first);
        }
    }

    selectTab(tab: Tab) {
        if (this.switchStrategy === "switch") {
            // deactivate all tabs
            this.tabs.toArray().forEach((tab) => (tab.active = false));

            // activate the tab the user has clicked on.
            tab.active = true;

            this.select.emit(tab.title);
        } else {
            this.switch.emit({ tabTitle: tab.title, tabCode: tab.code });
        }
    }
}
